import get from "lodash/get";
import keys from "lodash/keys";
import { getRecommendationMode, getVisiblePlansRecommendationMode } from "QuotePage/app/compactQuotePage/Selectors";
import { filterPlan } from "QuotePage/PlanFilters";
import { createSelector } from "reselect";
import _ from "underscore";
import { planMetalTierToNumber } from "Utils/plansUtils";

import { getCartMedicalPlanIds } from "Common/CartService/Selectors";
import {
  getPlans,
  getSortConfig,
  getSavedPlanIDs,
  getSavedPlans,
  getMedicalPlanEnrolledId,
  getMedicalPlanRenewalId,
  getPinnedPlans,
  getPlanDeviationProps,
  calculatePlanDeviationProps,
  isShowingExtraPlans,
} from "Common/PlanLoader/Selectors";

import { history } from "./Store";

import type { State } from "./Store";
import type {
  RecommendationSurvey,
  QuotePageAsyncInitialState,
  QuotePageRenewalPolicy,
  PlanPackage,
  PlanRangeProps,
  MedicalPlanPrice,
  EmployeeProfile,
  CombinedFilters,
  MedicalFilters,
} from "Utils/types";

export {
  getPlans,
  getSortConfig,
  getSavedPlanIDs,
  getSavedPlans,
  getMedicalPlanEnrolledId,
  getMedicalPlanRenewalId,
  getPinnedPlans,
  getPlanDeviationProps,
  calculatePlanDeviationProps,
  isShowingExtraPlans,
};

const getAsyncInitialState = (state: State): QuotePageAsyncInitialState => state.asyncInitialState;

// I'm sorry for this selector name. =(
// This should only be true if we are in the Quote Page, and the
// call to get the Default Plan Suite has returned but the call to
// get the Initial Data hasn't. This will cause the overlay to hide
// (See 'showOverlay' selector below).
// We use this flag for components that cannot be interactive after
// the overlay is hidden.
export const hasPlansBeforeInitialData: (arg0: State) => boolean = createSelector(
  [getPlans, getAsyncInitialState],
  (plans: Array<MedicalPlanPrice>, asyncInitialState: QuotePageAsyncInitialState): boolean => plans && plans.length > 0 && asyncInitialState.loading,
);

export const showOverlay: (arg0: State) => boolean = createSelector(
  [getPlans, getAsyncInitialState],
  (plans: Array<MedicalPlanPrice>, asyncInitialState: QuotePageAsyncInitialState) =>
    !asyncInitialState.loaded && asyncInitialState.loading && plans.length <= 0,
);

export const getCurrentPath = () => history.location.pathname || "/";
export const getFilters = (state: State): CombinedFilters => state.filters;
export const getFilterConfig: (arg0: State) => MedicalFilters = createSelector([getFilters], (filters: CombinedFilters): MedicalFilters => {
  // removing the 'shop_mode', and 'recommendation_mode' from the filters,
  // since they are not used as part of the FilterConfig object.
  const { shop_mode, recommendation_mode, dentalFilter, limitedValuePlansFilter, ...filterConfig } = filters;
  return filterConfig;
});

export function companyToken(state: State) {
  return state.companyProfile.id_token;
}

export function getPayrollLead(state: State) {
  return state.companyProfile?.payroll_lead;
}

export function getSortOrder(state: State) {
  const sortConfig = getSortConfig(state);

  if (sortConfig) return sortConfig;
  return "score";
}

export const getFilteredPlansRoutine = (filterConfig: MedicalFilters, sortOrder: string, plans: Array<MedicalPlanPrice>): Array<MedicalPlanPrice> => {
  if (sortOrder == "score") {
    sortOrder = "boosted_score";
  }
  // Shallow Copy plans array
  const plansCopy = plans.slice();

  // Filter
  const filteredPlans = _.chain(plansCopy).filter(_.partial(filterPlan, filterConfig)).value();

  // Sort
  const visiblePlans = _.chain(filteredPlans)
    .sortBy(_.partial(_.getPath, _, sortOrder))
    .value();

  return Object.freeze(visiblePlans);
};

export const getSortedPlansRoutine = (plans: Array<MedicalPlanPrice>): Array<MedicalPlanPrice> => {
  // Shallow Copy plans array
  const plansCopy = plans.slice();

  // Sort
  const visiblePlans = _.chain(plansCopy)
    .sortBy(_.partial(_.getPath, _, "boosted_score"))
    .value();

  return Object.freeze(visiblePlans);
};

export function getShopMode(state: State) {
  const shopMode = state.filters.shop_mode;

  if (shopMode && _.contains(shopMode.include, "plan_suite")) return "plan_suite";
  return "single_plan";
}

export const getCarriersInRegion: (arg0: State) => Array<string> = createSelector(
  [(state: State): Array<string> => state.carriersInRegion, getPlans],
  (carriersInRegion: Array<string>, plans: Array<MedicalPlanPrice>): Array<string> => carriersInRegion || _.uniq(plans.map((p) => p.carrier)).sort(),
);

export const getPlanPackages = (state: State): Array<PlanPackage> => state.planPackages || [];

export const getPlanPackageIdsById = (state: State, id: string): Array<string | null | undefined> => {
  const planPackages = getPlanPackages(state);
  const planPackage = planPackages.find((suite) => suite.id === id);
  return planPackage ? [planPackage.bronze, planPackage.silver, planPackage.gold, planPackage.platinum].filter((planId) => planId !== undefined) : [];
};

export const getPlanPackageIds: (arg0: State) => Array<Array<string | null | undefined>> = createSelector(
  [getPlanPackages],
  (planPackages: Array<PlanPackage>) => planPackages.map((planPackage) => [planPackage.bronze, planPackage.silver, planPackage.gold, planPackage.platinum]),
);

export const getFilteredPlans: (arg0: State) => Array<MedicalPlanPrice> = createSelector([getFilterConfig, getSortOrder, getPlans], getFilteredPlansRoutine);

export const getSortedPlans: (arg0: State) => Array<MedicalPlanPrice> = createSelector([getPlans], getSortedPlansRoutine);

// TODO(daniel-sep-5-2022) this should just be the plan ids and not a plan object.
export const getRecommendedPlanIDs = (state: State): Array<MedicalPlanPrice> => state.recommendedPlans || [];

export const getRecommendedPlans: (arg0: State) => Array<MedicalPlanPrice | null | undefined> = createSelector(
  [getRecommendedPlanIDs, getPlans],
  (recommendedPlanIDs: Array<MedicalPlanPrice>, plans: Array<MedicalPlanPrice>): Array<MedicalPlanPrice | null | undefined> =>
    recommendedPlanIDs.map((id) => {
      if (id === "multiple_plans") {
        return null;
      }
      return _.findWhere(plans, { id });
    }),
);

export const getCustomRecommendationByPlanId = createSelector(
  [(state) => state.recommendedPlans, (_state, planId) => planId],
  (recommendedPlans, planId) => recommendedPlans?.find((rec) => rec.id === planId)?.message,
);

export const getRecommendationIndexByPlanId = createSelector([(state) => state.recommendedPlans, (_state, planId) => planId], (recommendedPlans, planId) =>
  recommendedPlans.findIndex((rec) => rec.id === planId),
);

export const getVisiblePlans = (state: State): Array<MedicalPlanPrice> => {
  const currentPath = getCurrentPath();
  if (
    currentPath.match(/\/.+\/select-plan$/) ||
    currentPath.includes("select-plan/plan-details") ||
    currentPath.includes("select-plan/price-breakdown") ||
    currentPath.match(/\/.+\/oauth_callback$/) ||
    currentPath.includes("select-plan/plan-package")
  ) {
    if (getRecommendationMode(state) === "multiple_plans") {
      return getAllPlanSuitePlans(state);
    }
    if (getRecommendationMode(state) === "recommended") {
      return [...getVisiblePlansRecommendationMode(state), ...getAllPlanSuitePlans(state)];
    }
    return getVisiblePlansCompactQuotePage(state);
  }
  if (currentPath.includes("/compare-plans")) {
    return getVisiblePlansCompare(state);
  }
  if (currentPath.includes("/company-info") || currentPath.includes("/coverage-options")) {
    return getPlans(state);
  }
  throw new Error(`Unable to determine visible plans for ${JSON.stringify(currentPath)}`);
};

export const getVisiblePlansCompactQuotePage: (arg0: State) => Array<MedicalPlanPrice> = createSelector(
  [getFilteredPlans, getPinnedPlans, isShowingExtraPlans],
  (filteredPlans: Array<MedicalPlanPrice>, pinnedPlans: Array<MedicalPlanPrice>, isShowingExtraPlans: boolean): Array<MedicalPlanPrice> =>
    pinnedPlans.concat(filteredPlans.slice(0, isShowingExtraPlans ? filteredPlans.length : 20)),
);

export const getMecPlans = (state) => state.mecPlans;
export const getCheapestSmallGroupPlan = (state) => {
  const sortedPlans = state.allPlans.sort((a, b) => a.total_employee_premium - b.total_employee_premium);

  return sortedPlans[0] || {};
};

export const getMvpPlans = (state) => state.mvpPlans;

export const getHideablePlansCount: (arg0: State) => number = createSelector(
  [getFilteredPlans, getPinnedPlans],
  (filteredPlans: Array<MedicalPlanPrice>, pinnedPlans: Array<MedicalPlanPrice>): number => {
    const count = pinnedPlans.length + filteredPlans.length;
    return Math.max(count - 20, 0);
  },
);

const getVisiblePlansCompare: (arg0: State) => Array<MedicalPlanPrice> = createSelector(
  [getSavedPlans],
  (savedPlans: Array<MedicalPlanPrice>): Array<MedicalPlanPrice> => _.filter(savedPlans),
);

export const getAllPlanSuitePlans: (arg0: State) => Array<MedicalPlanPrice> = createSelector(
  [getPlanPackages, getPlans],
  (planPackages: Array<PlanPackage>, plans: Array<MedicalPlanPrice>): Array<MedicalPlanPrice> => {
    const planIds = [];
    planPackages.forEach((planPackage) => {
      for (const key in planPackage) {
        if (key !== "name") {
          planIds.push(planPackage[key]);
        }
      }
    });
    return _.filter(plans, (p) => _.contains(planIds, p.id));
  },
);

export const hasPlanSuiteSupport: (arg0: State) => boolean = createSelector([getPlanPackages], (planPackages: Array<PlanPackage>) => planPackages.length > 0);

export const getPlanRangeProps: (arg0: State) => PlanRangeProps = createSelector(
  [getPlans],
  (plans: Array<MedicalPlanPrice>): PlanRangeProps => ({
    minPremium: _.min(_.map(plans, (p) => p.average_monthly_premium)),
    maxPremium: _.max(_.map(plans, (p) => p.average_monthly_premium)),
    minDeductible: _.min(_.map(plans, (p) => p.deductible)),
    maxDeductible: _.max(_.map(plans, (p) => p.deductible)),
    minDoctorsCost: _.min(_.map(plans, (p) => p.estimated_costs.pcp_visit)),
    maxDoctorsCost: _.max(_.map(plans, (p) => p.estimated_costs.pcp_visit)),
    minGenericDrugCost: _.min(_.map(plans, (p) => p.estimated_costs.generic_drugs)),

    maxGenericDrugCost: _.max(_.map(plans, (p) => p.estimated_costs.generic_drugs)),

    minHospitalizationCost: _.min(_.map(plans, (p) => p.estimated_costs.hospitalization)),

    maxHospitalizationCost: _.max(_.map(plans, (p) => p.estimated_costs.hospitalization)),

    minEmergencyRoomVisitCost: _.min(_.map(plans, (p) => p.estimated_costs.emergency_room)),

    maxEmergencyRoomVisitCost: _.max(_.map(plans, (p) => p.estimated_costs.emergency_room)),
  }),
);

export const getPlanFromPathId: (arg0: State) => MedicalPlanPrice | null | undefined = createSelector(
  [getPlans, getCurrentPath],
  (plans: Array<MedicalPlanPrice>, pathname: string): MedicalPlanPrice | null | undefined => {
    const pathMatch = pathname.match(/\/.+\/plan-details\/(\d+)/);
    const planId = pathMatch && pathMatch[1];
    const plan = plans.find((p) => p.id == planId);
    return plan;
  },
);

export const getPathId: (arg0: State) => string | null | undefined = createSelector([getCurrentPath], (pathname: string): string | null | undefined => {
  const pathMatch = pathname.match(/\/.+\/plan-details\/(\d+)/);
  const planId = pathMatch && pathMatch[1];
  return planId;
});

export const getPlanPosition = createSelector([getVisiblePlansCompactQuotePage, (_state, planID) => planID], (visiblePlans, planID) => {
  const planIndex = visiblePlans.findIndex((p) => p.id === planID);
  return planIndex + 1; // add one to index for human-legible position
});

const getRenewalPolicy = (state: State): QuotePageRenewalPolicy => state.renewalPolicy;

// This selector is specific for cards displyaed in the Quote Page / Cart Page.
export const getCardRole: (arg0: State) => string = createSelector([getRenewalPolicy], (renewalPolicy: QuotePageRenewalPolicy): string =>
  renewalPolicy ? "renewal" : "application",
);

export const getSortedPlanSuiteBasePlans: (arg0: State) => Array<MedicalPlanPrice> = createSelector(
  [getPlanPackages, getPlans],
  (planPackages: Array<PlanPackage>, plans: Array<MedicalPlanPrice>): Array<MedicalPlanPrice> => {
    const bronzePlanIDs = planPackages.map((planPackage) => planPackage.bronze);
    const bronzePlans = [];
    bronzePlanIDs.forEach((planId) => {
      const plan = plans.find((p) => p.id == planId);
      if (plan) {
        bronzePlans.push(plan);
      }
    });
    return _.sortBy(bronzePlans, (p) => p.average_monthly_premium);
  },
);

export const getIsCartPlan = createSelector([getCartMedicalPlanIds, getPlans, (_state, planID) => planID], (cartMedicalPlanIds, _plans, planID) =>
  cartMedicalPlanIds?.includes(planID),
);

export const getCartBasePlan: (arg0: State) => MedicalPlanPrice | null | undefined = createSelector(
  [getPlans, getCartMedicalPlanIds],
  (plans: Array<MedicalPlanPrice>, cartMedicalPlanIds: Array<string>): MedicalPlanPrice | null | undefined => {
    if (cartMedicalPlanIds.length > 0) {
      return plans.find((plan) => plan.id === cartMedicalPlanIds[0]);
    }
  },
);

export const hasBasePlanSelected: (arg0: State) => boolean = createSelector(
  [getAllPlanSuitePlans, getCartBasePlan],
  (supportedPlans: Array<MedicalPlanPrice>, basePlan: MedicalPlanPrice | null | undefined): boolean => {
    if (!basePlan) return false;
    const supportedPlanIds = supportedPlans.map((x) => x.id);
    return _.contains(supportedPlanIds, basePlan.id);
  },
);

export const getUpgradeOptions: (arg0: State) => Array<MedicalPlanPrice> = createSelector(
  [getPlans, getPlanPackages, getCartBasePlan, hasBasePlanSelected],
  (
    plans: Array<MedicalPlanPrice>,
    suites: Array<PlanPackage>,
    basePlan: MedicalPlanPrice | null | undefined,
    hasSupportedBasePlan: boolean,
  ): Array<MedicalPlanPrice> => {
    if (plans.length === 0 || suites.length === 0 || !basePlan || !hasSupportedBasePlan) {
      return [];
    }
    const carrierOptions = suites.find((s) => {
      for (const key in s) {
        if (s[key] === basePlan?.id) {
          return true;
        }
      }
    });
    const { silver, gold, platinum } = carrierOptions || {};
    let metalTier = "bronze";
    for (const key in carrierOptions) {
      if (carrierOptions[key] === basePlan.id) {
        metalTier = key;
      }
    }
    let planIds;
    switch (metalTier) {
      case "bronze":
        planIds = [silver, gold, platinum];
        break;
      case "silver":
        planIds = [gold, platinum];
        break;
      case "gold":
        planIds = [platinum];
        break;
      default:
        console.warn("Platinum base plan is not allowed.");
        break;
    }

    return _.sortBy(
      _.filter(plans, (p) => _.contains(planIds, p.id)),
      (p) => planMetalTierToNumber(p),
    );
  },
);

export const calculateNumEmployeeProfiles = (employeeProfile: EmployeeProfile = []) =>
  employeeProfile.reduce((acc, curr) => acc + (curr && curr.age ? 1 : 0), 0);

export const calculateNumDependents = (employeeProfile: EmployeeProfile = []) =>
  employeeProfile.reduce((acc, curr) => {
    let numDependents = 0;
    if (curr) {
      numDependents += curr.spouse_age ? 1 : 0;
      numDependents += curr.children ? curr.children.reduce((a, c) => a + (c || c === 0 ? 1 : 0), 0) : 0;
    }
    return acc + numDependents;
  }, 0);

export const getEmployeeProfile = (state: State): EmployeeProfile => state.employeeProfile;

export const getNumEmployeeProfiles = (state: State): number => calculateNumEmployeeProfiles(state.employeeProfile);

export const getNumDependents = (state: State): number => calculateNumDependents(state.employeeProfile);

// just get the first survey with a valid key, else return null
export const getRecommendationSurvey = (state: State): RecommendationSurvey | null | undefined => {
  const validKeys = keys(get(state, "entities.recommendationSurveys")).filter((key) => key !== "undefined");

  if (validKeys && validKeys.length > 0) {
    return get(state, "entities.recommendationSurveys")[validKeys[0]];
  }

  return null;
};

export function getLimitedValuePlansFilterConfig(state: State) {
  const { limitedValuePlansFilter, ..._rest } = state.filters;
  return limitedValuePlansFilter;
}
